import React, { useEffect, useState, useCallback } from 'react';
import { FileText, Plus, Trash2, Loader2, Save, Eye, Download, Globe, Mail, ArrowLeft, FileType } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import RichTextEditor from '../components/RichTextEditor';
import ArticleEmailModal from './ArticleEmailModal';
import { sanitizeHtml } from '../lib/sanitizeHtml';
import LinkAuditButton from '../components/LinkAuditButton';

const NEW = { name: '', kind: 'template', subject: '', body: '' };

export default function ArticlesTab() {
  const auth = useAuth();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null=list view, else article object
  const [datasets, setDatasets] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [preview, setPreview] = useState(null);
  const [emailing, setEmailing] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    auth.listArticles().then((d) => setList(Array.isArray(d) ? d : [])).catch(() => setList([])).finally(() => setLoading(false));
  }, [auth]);

  useEffect(() => {
    load();
    auth.getArticleFields().then((d) => {
      window.__ARTICLE_FIELDS__ = d.fields || [];
      setDatasets(d.datasets || []);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openNew = () => { setPreview(null); setMsg(''); setEditing({ ...NEW }); };
  const openEdit = async (a) => { setPreview(null); setMsg(''); const full = await auth.getArticle(a.id); setEditing(full); };

  const save = async () => {
    if (!editing.name.trim()) { setMsg('Please give the article a name.'); return; }
    setBusy(true); setMsg('');
    const payload = { name: editing.name.trim(), kind: editing.kind, subject: editing.subject || '', body: editing.body || '' };
    try {
      const saved = editing.id ? await auth.updateArticle(editing.id, payload) : await auth.createArticle(payload);
      setEditing(saved); setMsg('Saved'); load();
      setTimeout(() => setMsg(''), 2000);
    } catch (e) { setMsg('Could not save.'); } finally { setBusy(false); }
  };

  const del = async (a) => { if (!window.confirm(`Delete "${a.name}"?`)) return; await auth.deleteArticle(a.id); load(); };

  const doPreview = async () => {
    setBusy(true);
    try { const p = await auth.previewArticle({ body: editing.body || '', subject: editing.subject || '' }); setPreview(p); }
    finally { setBusy(false); }
  };

  const doExport = async (format) => {
    setBusy(true);
    try {
      const blob = await auth.exportArticle({ body: editing.body || '', subject: editing.subject || '' }, format);
      const ext = { pdf: 'pdf', html: 'html', txt: 'txt', md: 'md' }[format] || 'txt';
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `${(editing.name || 'article').replace(/\s+/g, '_')}.${ext}`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } finally { setBusy(false); }
  };

  const publishPage = async () => {
    const title = window.prompt('Publish as a page — page title:', editing.name || '');
    if (!title) return;
    setBusy(true);
    try {
      const res = await auth.publishArticlePage({ title, body: editing.body || '' });
      setMsg(`Published at ${res.path}`);
      await auth.fetchPages();
    } catch (e) { setMsg('Could not publish.'); } finally { setBusy(false); }
  };

  // ---- List view ----
  if (!editing) {
    return (
      <div className="max-w-4xl" data-testid="articles-panel">
        <div className="flex items-center gap-2 mb-1"><FileText size={18} style={{ color: 'var(--maroon)' }} /><h2 className="font-title text-xl" style={{ color: 'var(--maroon)' }}>Articles</h2></div>
        <p className="text-sm mb-4" style={{ color: 'var(--olive)' }}>
          Write rich articles once and reuse them. Save as a <strong>template</strong> (with <code>{'{{fields}}'}</code> mailmerge) or a finished <strong>document</strong>.
          Export as text, Markdown, HTML or PDF, publish as a website page, or email it to selected members with each person's details merged in.
        </p>
        <button onClick={openNew} data-testid="article-new-btn" className="inline-flex items-center gap-1 px-3 py-2 rounded text-sm font-title mb-3" style={{ background: 'var(--maroon)', color: 'var(--cream)' }}><Plus size={15} /> New article</button>
        <a href="/admin/templates" data-testid="system-templates-link" className="inline-flex items-center gap-1 px-3 py-2 rounded text-sm font-title mb-3 ml-2" style={{ background: 'var(--navy)', color: 'var(--cream)' }}><FileType size={15} /> Report &amp; Output Templates</a>
        <LinkAuditButton />
        <div className="bg-white rounded-lg border shadow-sm overflow-x-auto" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
          <table className="w-full text-sm" data-testid="articles-table">
            <thead><tr style={{ background: 'var(--navy)' }}>
              <th className="px-3 py-2.5 text-left font-title" style={{ color: 'var(--cream)' }}>Name</th>
              <th className="px-3 py-2.5 text-left font-title" style={{ color: 'var(--cream)' }}>Kind</th>
              <th className="px-3 py-2.5 text-right font-title" style={{ color: 'var(--cream)' }}>Actions</th>
            </tr></thead>
            <tbody>
              {loading ? <tr><td colSpan={3} className="px-3 py-4 text-center"><Loader2 className="animate-spin inline" size={16} /></td></tr>
              : list.length === 0 ? <tr><td colSpan={3} className="px-3 py-4 text-center" style={{ color: 'var(--olive)' }}>No articles yet.</td></tr>
              : list.map((a) => (
                <tr key={a.id} className="border-t" style={{ borderColor: 'rgba(0,0,0,0.06)' }} data-testid={`article-row-${a.id}`}>
                  <td className="px-3 py-2 font-title" style={{ color: 'var(--navy)' }}>{a.name}</td>
                  <td className="px-3 py-2 capitalize">{a.kind}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button onClick={() => openEdit(a)} data-testid={`article-edit-${a.id}`} className="mr-3 text-sm underline" style={{ color: 'var(--maroon)' }}>Open</button>
                    <button onClick={() => del(a)} data-testid={`article-delete-${a.id}`}><Trash2 size={15} style={{ color: '#8a1f11' }} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // ---- Editor view ----
  return (
    <div className="max-w-4xl" data-testid="article-editor-view">
      <button onClick={() => { setEditing(null); load(); }} data-testid="article-back" className="inline-flex items-center gap-1 text-sm mb-3" style={{ color: 'var(--olive)' }}><ArrowLeft size={15} /> Back to articles</button>

      <div className="bg-white rounded-lg border shadow-sm p-4 space-y-3" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
        <div className="grid sm:grid-cols-3 gap-3">
          <div className="sm:col-span-2">
            <label className="block text-sm font-title mb-1" style={{ color: 'var(--navy)' }}>Name</label>
            <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="article-name" className="w-full px-3 py-2 rounded border text-sm" style={{ borderColor: 'rgba(0,0,0,0.2)' }} />
          </div>
          <div>
            <label className="block text-sm font-title mb-1" style={{ color: 'var(--navy)' }}>Kind</label>
            <select value={editing.kind} onChange={(e) => setEditing({ ...editing, kind: e.target.value })} data-testid="article-kind" className="w-full px-3 py-2 rounded border text-sm" style={{ borderColor: 'rgba(0,0,0,0.2)' }}>
              <option value="template">Template (reusable, with fields)</option>
              <option value="document">Document (filled/finished)</option>
            </select>
          </div>
        </div>
        <div>
          <label className="block text-sm font-title mb-1" style={{ color: 'var(--navy)' }}>Email subject (optional — supports <code>{'{{fields}}'}</code>)</label>
          <input value={editing.subject} onChange={(e) => setEditing({ ...editing, subject: e.target.value })} placeholder="e.g. Hello {{first_name}}" data-testid="article-subject" className="w-full px-3 py-2 rounded border text-sm" style={{ borderColor: 'rgba(0,0,0,0.2)' }} />
        </div>
        <div>
          <label className="block text-sm font-title mb-1" style={{ color: 'var(--navy)' }}>Article</label>
          <RichTextEditor value={editing.body} onChange={(html) => setEditing({ ...editing, body: html })} datasets={datasets} />
        </div>

        <div className="flex flex-wrap items-center gap-2 pt-1">
          <button onClick={save} disabled={busy} data-testid="article-save" className="inline-flex items-center gap-1 px-4 py-2 rounded text-sm font-title" style={{ background: 'var(--maroon)', color: 'var(--cream)', opacity: busy ? 0.7 : 1 }}>{busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save</button>
          <button onClick={doPreview} disabled={busy} data-testid="article-preview" className="inline-flex items-center gap-1 px-3 py-2 rounded text-sm" style={{ background: 'var(--sage)' }}><Eye size={14} /> Preview (merged)</button>
          <span className="w-px h-6" style={{ background: 'rgba(0,0,0,0.12)' }} />
          <button onClick={() => doExport('txt')} disabled={busy} data-testid="article-export-txt" className="inline-flex items-center gap-1 px-3 py-2 rounded text-sm" style={{ background: 'var(--sage)' }}><Download size={14} /> Text</button>
          <button onClick={() => doExport('md')} disabled={busy} data-testid="article-export-md" className="inline-flex items-center gap-1 px-3 py-2 rounded text-sm" style={{ background: 'var(--sage)' }}><Download size={14} /> MD</button>
          <button onClick={() => doExport('html')} disabled={busy} data-testid="article-export-html" className="inline-flex items-center gap-1 px-3 py-2 rounded text-sm" style={{ background: 'var(--sage)' }}><Download size={14} /> HTML</button>
          <button onClick={() => doExport('pdf')} disabled={busy} data-testid="article-export-pdf" className="inline-flex items-center gap-1 px-3 py-2 rounded text-sm" style={{ background: 'var(--sage)' }}><FileType size={14} /> PDF</button>
          <span className="w-px h-6" style={{ background: 'rgba(0,0,0,0.12)' }} />
          <button onClick={publishPage} disabled={busy} data-testid="article-publish" className="inline-flex items-center gap-1 px-3 py-2 rounded text-sm font-title" style={{ background: 'var(--navy)', color: 'var(--cream)' }}><Globe size={14} /> Publish as page</button>
          <button onClick={() => setEmailing(true)} disabled={!editing.id} title={editing.id ? '' : 'Save first'} data-testid="article-email" className="inline-flex items-center gap-1 px-3 py-2 rounded text-sm font-title" style={{ background: 'var(--navy)', color: 'var(--cream)', opacity: editing.id ? 1 : 0.5 }}><Mail size={14} /> Email this</button>
          {msg && <span className="text-sm" style={{ color: msg.startsWith('Could') ? '#8a1f11' : '#1a7f37' }} data-testid="article-msg">{msg}</span>}
        </div>
      </div>

      {preview && (
        <div className="bg-white rounded-lg border shadow-sm p-4 mt-4" style={{ borderColor: 'rgba(0,0,0,0.08)' }} data-testid="article-preview-panel">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-title text-sm" style={{ color: 'var(--navy)' }}>Preview (merged with your own details as a sample){preview.subject ? ` — “${preview.subject}”` : ''}</h3>
            <button onClick={() => setPreview(null)} className="text-xs underline" style={{ color: 'var(--olive)' }}>close</button>
          </div>
          <div className="prose prose-sm max-w-none border-t pt-3" style={{ borderColor: 'rgba(0,0,0,0.08)' }} dangerouslySetInnerHTML={{ __html: sanitizeHtml(preview.html) }} />
        </div>
      )}

      {emailing && (
        <ArticleEmailModal article={editing} onClose={() => setEmailing(false)} />
      )}
    </div>
  );
}
